import AssetImage from "@/components/AssetImage";
import { useGetAsset } from "@/hooks/useAsset";
import { formatChallengeDateRange } from "@/utils/formatChallengeDateRange";
import { ChallengeResponseWithAcceptance } from "@devour/client";
import {FC, useEffect, useState} from "react";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router";
import {Button} from "@/components/buttons/Button";
import {isColorDarkOrLight} from "@/utils/isColorDarkOrLight";
import classNames from "classnames";
import {DateTime} from "luxon";

interface Props {
    challenge: ChallengeResponseWithAcceptance;
}

export function getCurrentEstTime() {
    return DateTime.now().setZone("America/New_York")
        .toMillis();
}

export const OverwolfGoVipChallengeCard: FC<Props> = ({ challenge }) => {
    // Use CDN URL if available, otherwise fetch asset
    const backgroundImage = isMobile ? challenge.backgroundImageSmall : challenge.backgroundImageLarge;
    const backgroundImageUrl = isMobile ? challenge.backgroundImageSmallUrl : challenge.backgroundImageLargeUrl;
    const { data } = useGetAsset(backgroundImageUrl ? null : backgroundImage as any);
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(getCurrentEstTime());

    useEffect(() => {
        const currentTimeIntervalKey = setInterval(() => setCurrentDate(getCurrentEstTime()), 1000 * 10);
        return () => clearInterval(currentTimeIntervalKey);
    }, []);

    const handleChallengeClick = () => {
        if (challenge.landingPageUrl) {
            window.open(challenge.landingPageUrl, "_blank", "noopener,noreferrer");
        } else {
            navigate(challenge.brandMapSlug ? `/brands/${challenge.brandMapSlug}` : `/challenges/${challenge.id}`);
        }
    };

    const getChallengeLabel = () => {
        if (challenge.end <= currentDate) {
            return "View";
        }
        if (challenge.isAccepted) {
            return "Track";
        }
        return "Explore";
    };

    // Use CDN URL directly if available, otherwise use fetched asset
    const finalBackgroundUrl = backgroundImageUrl || data?.cdnUrl || "";

    return (
        <div
            className="overwolf-go-vip-challenges_card"
            style={{backgroundImage: `url(${finalBackgroundUrl})`}}
        >
            <div className="overwolf-go-vip-challenges_card_shader" />
            <div className="overwolf-go-vip-challenges_card_heading">
                <div className="overwolf-go-vip-challenges_card_heading_info">
                    {/* Use CDN URL directly if available, otherwise fall back to AssetImage */}
                    {challenge.brandLogoUrl
                        ? <img src={challenge.brandLogoUrl} alt="brand-logo" />
                        : <AssetImage assetId={challenge.brandLogo as any} alt="brand-logo"/>
                    }
                    <span>{formatChallengeDateRange(challenge.start, challenge.end)}</span>
                </div>
                <Button variant="primary"
                    onClick={handleChallengeClick}
                    {...{style: {backgroundColor: challenge.brandColor}}}
                    className={classNames("overwolf-go-vip-challenges_card_heading_action", isColorDarkOrLight(challenge.brandColor))}
                >
                    {getChallengeLabel()}
                </Button>
            </div>

            <div className="overwolf-go-vip-challenges_card_main">
                <h5>{challenge.cardTitle || challenge.name}</h5>
                <span>{challenge.description}</span>
            </div>
        </div>
    );
};
